/**
 * Sales Sessions List Page
 * =========================
 * Paginated list of all sessions with search, filter, and sort capabilities
 *
 * Features:
 * - Search by restaurant name
 * - Filter by status (all, pending, activated, expired, ready, processing, failed)
 * - Sort options (newest, oldest, name)
 * - Pagination controls
 * - Action buttons per session
 * - Responsive table design
 *
 * Security & Authorization:
 * ------------------------
 * - Backend filters by sales_user_id from authenticated JWT token
 * - sessions endpoint: WHERE sales_rep_user_id = $1 (sales_routes.py:839)
 * - Each sales rep can ONLY see their own sessions
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Send,
  Loader2,
  AlertCircle,
  FileText,
  Trash2,
  Copy,
  Check,
  X,
  Link as LinkIcon,
  LayoutDashboard,
  Edit,
  Plus,
  Phone,
  RefreshCw,
  DollarSign
} from 'lucide-react';
import { salesApi, SessionsResponse } from '../services/api/salesApi';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const SalesSessionsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [sessions, setSessions] = useState<SessionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal state for activation link
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [activationLinkData, setActivationLinkData] = useState<{
    link: string;
    restaurantName: string;
    expiresAt: string;
    sessionId: string;
  } | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Email sending state
  const [customerEmail, setCustomerEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Delete confirmation modal
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<any>(null);

  // Error modal
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Pricing modal state
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingSession, setPricingSession] = useState<any>(null);
  const [pricingData, setPricingData] = useState<{
    tier_1_fee: number;
    tier_2_fee: number;
    tier_3_fee: number;
    overage_rate: number;
    minutes_included_override: number | null;
    is_custom: boolean;
    notes: string;
  } | null>(null);
  const [pricingEdits, setPricingEdits] = useState({
    tier_1_fee: '',
    tier_2_fee: '',
    tier_3_fee: '',
    overage_rate: '',
    minutes_included_override: '',
    notes: ''
  });
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filterStatus, setFilterStatus] = useState(searchParams.get('filter') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'newest');
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page') || '1'));

  const pageSize = 10;

  // Load sessions
  useEffect(() => {
    loadSessions();
  }, [searchQuery, filterStatus, sortBy, currentPage]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await salesApi.getSessions({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
        filter_status: filterStatus,
        sort: sortBy
      });

      setSessions(response);

      // Update URL params
      const params: any = {};
      if (searchQuery) params.search = searchQuery;
      if (filterStatus !== 'all') params.filter = filterStatus;
      if (sortBy !== 'newest') params.sort = sortBy;
      if (currentPage > 1) params.page = currentPage.toString();
      setSearchParams(params);

    } catch (err: any) {
      console.error('Failed to load sessions:', err);
      setError(err.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  // Handle search with debounce
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter change
  const handleFilterChange = (value: string) => {
    setFilterStatus(value);
    setCurrentPage(1);
  };

  // Handle sort change
  const handleSortChange = (value: string) => {
    setSortBy(value);
    setCurrentPage(1);
  };

  // Status badge
  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-400/20 text-yellow-300 border-yellow-400/30', label: 'Pending' },
      activated: { color: 'bg-green-400/20 text-green-300 border-green-400/30', label: 'Activated' },
      expired: { color: 'bg-red-400/20 text-red-300 border-red-400/30', label: 'Expired' },
      ready: { color: 'bg-blue-400/20 text-blue-300 border-blue-400/30', label: 'Ready' },
      processing: { color: 'bg-purple-400/20 text-purple-300 border-purple-400/30', label: 'Processing' },
      failed: { color: 'bg-red-400/20 text-red-300 border-red-400/30', label: 'Failed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

    return (
      <span className={`px-2 py-1 rounded border text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Handle Generate Invite - Call API and show activation link modal
  const handleGenerateInvite = async (session: any) => {
    try {
      const salesUser = JSON.parse(localStorage.getItem('sales_user') || '{}');

      const response = await fetch('/api/sales/generate-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          session_id: session.session_id,
          restaurant_email: session.restaurant_email || 'pending@dineai.ai',
          restaurant_name: session.restaurant_name,
          sales_rep_name: salesUser.full_name || salesUser.email
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate invite');
      }

      const data = await response.json();

      // Show modal with activation link
      setActivationLinkData({
        link: data.activation_link,
        restaurantName: session.restaurant_name,
        expiresAt: data.expires_at,
        sessionId: session.session_id
      });
      setShowActivationModal(true);
      setLinkCopied(false);
      // Reset email state
      setCustomerEmail('');
      setEmailSent(false);
      setEmailError('');

      // Reload sessions to update status
      loadSessions();

    } catch (err: any) {
      console.error('Failed to generate invite:', err);
      setErrorMessage(err.message || 'Failed to generate activation link. Please try again.');
      setShowErrorModal(true);
    }
  };

  // Handle Renew Invite - Extend expired activation link
  const handleRenewInvite = async (session: any) => {
    try {
      const response = await fetch('/api/sales/renew-invite', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          session_id: session.session_id
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to renew invite');
      }

      const data = await response.json();

      // Show modal with renewed activation link
      setActivationLinkData({
        link: data.activation_link,
        restaurantName: session.restaurant_name,
        expiresAt: data.expires_at,
        sessionId: session.session_id
      });
      setShowActivationModal(true);
      setLinkCopied(false);
      // Reset email state
      setCustomerEmail('');
      setEmailSent(false);
      setEmailError('');

      // Reload sessions to update status (should now show "pending" instead of "expired")
      loadSessions();

    } catch (err: any) {
      console.error('Failed to renew invite:', err);
      setErrorMessage(err.message || 'Failed to renew activation link. Please try again.');
      setShowErrorModal(true);
    }
  };

  // Handle copy activation link
  const handleCopyLink = async () => {
    if (activationLinkData) {
      await navigator.clipboard.writeText(activationLinkData.link);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Handle send activation email
  const handleSendEmail = async () => {
    if (!activationLinkData || !customerEmail) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    try {
      setSendingEmail(true);
      setEmailError('');

      const response = await fetch('/api/sales/send-activation-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          session_id: activationLinkData.sessionId,
          customer_email: customerEmail
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send email');
      }

      setEmailSent(true);
      setEmailError('');
    } catch (err: any) {
      console.error('Failed to send activation email:', err);
      setEmailError(err.message || 'Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  // Handle Open Dashboard - Navigate to restaurant dashboard
  const handleOpenDashboard = async (session: any) => {
    try {
      // Call prepare-menu-for-demo to get/create demo session
      const response = await fetch('/api/sales/prepare-menu-for-demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          session_id: session.session_id,
          restaurant_name: session.restaurant_name,
          restaurant_phone: null,
          restaurant_email: null,
          restaurant_address: null,
          restaurant_zipcode: '00000',
          location_name: 'Main Location',
          sales_rep_user_id: JSON.parse(localStorage.getItem('sales_user') || '{}').sales_user_id,
          sales_rep_email: JSON.parse(localStorage.getItem('sales_user') || '{}').email
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to open dashboard');
      }

      const data = await response.json();

      // Set demo session cookie
      document.cookie = `auth_token=${data.demo_session_id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

      // Mark as demo user to bypass JWT validation
      localStorage.setItem('isDemo', 'true');

      // Redirect to restaurant dashboard (sales rep can access Edit Menu, AI Images, etc. from there)
      window.location.href = `/restaurant-dashboard`;

    } catch (err: any) {
      console.error('Failed to open dashboard:', err);
      setErrorMessage(err.message || 'Failed to open restaurant dashboard. Please try again.');
      setShowErrorModal(true);
    }
  };

  // Handle Edit Menu - Navigate directly to menu review page (same as AI Host pattern)
  const handleEditMenu = async (session: any) => {
    try {
      // For ACTIVATED restaurants with rest_id, use admin dashboard access instead of temp menus
      // This ensures we edit the actual production menu, not temp storage
      if (session.status === 'activated' && session.rest_id && session.rest_account_id) {
        // Get admin dashboard access which creates proper auth token
        const adminResponse = await salesApi.getAdminDashboardAccess(session.rest_account_id);
        if (adminResponse.success) {
          // Open menu review directly using the dashboard URL pattern
          // The admin access already set up proper auth
          window.open(`${adminResponse.dashboard_url.replace('/restaurant-dashboard', '/restaurant-menu-review')}?rest_id=${session.rest_id}`, '_blank');
          return;
        }
      }

      // For non-activated sessions, use prepare-menu-for-demo flow
      // This creates temp restaurant structure for preview/editing
      const response = await fetch('/api/sales/prepare-menu-for-demo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          session_id: session.session_id,
          restaurant_name: session.restaurant_name,
          restaurant_phone: null,
          restaurant_email: null,
          restaurant_address: null,
          restaurant_zipcode: '00000',
          location_name: 'Main Location',
          sales_rep_user_id: JSON.parse(localStorage.getItem('sales_user') || '{}').sales_user_id,
          sales_rep_email: JSON.parse(localStorage.getItem('sales_user') || '{}').email
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to prepare menu');
      }

      const data = await response.json();

      // Set the demo session cookie for THIS restaurant
      document.cookie = `auth_token=${data.demo_session_id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

      // Set demo mode so menu review works with sales auth
      localStorage.setItem('isDemo', 'true');
      localStorage.setItem('sales_edit_session_id', session.session_id);

      // Navigate to menu review page with correct IDs from response
      navigate(`/restaurant-menu-review?processing_id=${data.menu_id}&rest_id=${data.rest_account_id}`);

    } catch (err: any) {
      console.error('Failed to open menu editor:', err);
      setErrorMessage(err.message || 'Failed to open menu editor. Please try again.');
      setShowErrorModal(true);
    }
  };

  // Handle Delete - Show confirmation dialog
  const handleDelete = (session: any) => {
    setSessionToDelete(session);
    setShowDeleteDialog(true);
  };

  // Handle Admin Dashboard Access - Opens restaurant dashboard for support
  const handleAdminDashboard = async (session: any) => {
    if (!session.rest_account_id) {
      setErrorMessage('Restaurant account not found. Session may not be activated yet.');
      setShowErrorModal(true);
      return;
    }

    try {
      const response = await salesApi.getAdminDashboardAccess(session.rest_account_id);
      if (response.success) {
        // Open dashboard in new tab
        window.open(response.dashboard_url, '_blank');
      }
    } catch (err: any) {
      console.error('Failed to get dashboard access:', err);
      setErrorMessage(err.response?.data?.detail || err.message || 'Failed to access dashboard');
      setShowErrorModal(true);
    }
  };

  // Upsell: Add Virtual Host to existing Virtual Assistant customer
  // Navigate to plan selection so customer can choose their plan
  const handleUpsellVirtualHost = (session: any) => {
    if (!session.rest_account_id) {
      setErrorMessage('Restaurant account not found.');
      setShowErrorModal(true);
      return;
    }

    // Store upsell context in localStorage for PlanSelection to pick up
    localStorage.setItem('upsell_rest_account_id', String(session.rest_account_id));
    localStorage.setItem('upsell_product_type', 'virtual_host');
    localStorage.setItem('upsell_restaurant_name', session.restaurant_name);
    localStorage.setItem('selected_product_type', 'virtual_host');

    // Navigate to plan selection with upsell mode
    navigate('/plan-selection?mode=upsell&product=virtual_host');
  };

  // Upsell: Add Virtual Assistant to existing Virtual Host customer
  // Navigate to plan selection so customer can choose their plan
  const handleUpsellVirtualAssistant = (session: any) => {
    if (!session.rest_account_id) {
      setErrorMessage('Restaurant account not found.');
      setShowErrorModal(true);
      return;
    }

    // Store upsell context in localStorage for PlanSelection to pick up
    localStorage.setItem('upsell_rest_account_id', String(session.rest_account_id));
    localStorage.setItem('upsell_product_type', 'virtual_assistant');
    localStorage.setItem('upsell_restaurant_name', session.restaurant_name);
    localStorage.setItem('selected_product_type', 'virtual_assistant');

    // Navigate to plan selection with upsell mode
    navigate('/plan-selection?mode=upsell&product=virtual_assistant');
  };

  // Confirm deletion - Execute delete after confirmation
  const confirmDelete = async () => {
    if (!sessionToDelete) return;

    try {
      const response = await fetch(`/api/sales/session/${sessionToDelete.session_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete session');
      }

      // Close delete dialog
      setShowDeleteDialog(false);
      setSessionToDelete(null);

      // Refresh sessions list
      await loadSessions();

      // Show success message
      setSuccessMessage(`Successfully deleted "${sessionToDelete.restaurant_name}"`);
      setShowSuccessModal(true);

    } catch (err: any) {
      console.error('Failed to delete session:', err);

      // Close delete dialog
      setShowDeleteDialog(false);
      setSessionToDelete(null);

      // Show error message
      setErrorMessage(err.message || 'Failed to delete session. Please try again.');
      setShowErrorModal(true);
    }
  };

  // Cancel deletion
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setSessionToDelete(null);
  };

  // Handle opening pricing modal - fetch current pricing config
  const handleOpenPricing = async (session: any) => {
    if (!session.rest_account_id) {
      setErrorMessage('Restaurant account not found. Session may not be activated yet.');
      setShowErrorModal(true);
      return;
    }

    setPricingSession(session);
    setShowPricingModal(true);
    setLoadingPricing(true);

    try {
      const response = await fetch(`/api/sales/pricing/${session.rest_account_id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load pricing configuration');
      }

      const data = await response.json();
      setPricingData(data);
      setPricingEdits({
        tier_1_fee: data.tier_1_fee.toFixed(2),
        tier_2_fee: data.tier_2_fee.toFixed(2),
        tier_3_fee: data.tier_3_fee.toFixed(2),
        overage_rate: data.overage_rate.toFixed(2),
        minutes_included_override: data.minutes_included_override ? String(data.minutes_included_override) : '',
        notes: data.notes || ''
      });
    } catch (err: any) {
      console.error('Failed to load pricing:', err);
      setShowPricingModal(false);
      setErrorMessage(err.message || 'Failed to load pricing configuration');
      setShowErrorModal(true);
    } finally {
      setLoadingPricing(false);
    }
  };

  // Handle saving pricing changes
  const handleSavePricing = async () => {
    if (!pricingSession?.rest_account_id) return;

    // Validate inputs
    const tier1 = parseFloat(pricingEdits.tier_1_fee);
    const tier2 = parseFloat(pricingEdits.tier_2_fee);
    const tier3 = parseFloat(pricingEdits.tier_3_fee);
    const overage = parseFloat(pricingEdits.overage_rate);

    if (isNaN(tier1) || isNaN(tier2) || isNaN(tier3) || isNaN(overage)) {
      setErrorMessage('Please enter valid numbers for all fee fields');
      setShowErrorModal(true);
      return;
    }

    if (tier1 < 0.50 || tier2 < 0.50 || tier3 < 0.50) {
      setErrorMessage('Minimum fee is $0.50 for all tiers');
      setShowErrorModal(true);
      return;
    }

    if (tier1 > 1.99 || tier2 > 1.99 || tier3 > 1.99) {
      const badValue = [tier1, tier2, tier3].find(v => v > 1.99) || tier1;
      setErrorMessage('Maximum fee is $1.99 per tier. Did you mean $' + (badValue / 100).toFixed(2) + '?');
      setShowErrorModal(true);
      return;
    }

    if (overage < 0.05) {
      setErrorMessage('Minimum overage rate is $0.05');
      setShowErrorModal(true);
      return;
    }

    if (overage > 0.10) {
      setErrorMessage('Maximum overage rate is $0.10/min. Did you mean $' + (overage / 100).toFixed(2) + '?');
      setShowErrorModal(true);
      return;
    }

    // Validate minutes override
    const minutesOverride = pricingEdits.minutes_included_override ? parseInt(pricingEdits.minutes_included_override) : null;
    if (minutesOverride !== null) {
      if (minutesOverride < 100) {
        setErrorMessage('Minutes override must be at least 100');
        setShowErrorModal(true);
        return;
      }
      if (minutesOverride > 5000) {
        setErrorMessage('Minutes override cannot exceed 5,000');
        setShowErrorModal(true);
        return;
      }
    }

    setSavingPricing(true);

    try {
      const response = await fetch(`/api/sales/pricing/${pricingSession.rest_account_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        },
        body: JSON.stringify({
          tier_1_fee: tier1,
          tier_2_fee: tier2,
          tier_3_fee: tier3,
          overage_rate: overage,
          minutes_included_override: pricingEdits.minutes_included_override ? parseInt(pricingEdits.minutes_included_override) : null,
          notes: pricingEdits.notes || null
        })
      });

      if (!response.ok) {
        const error = await response.json();
        let message = 'Failed to save pricing';
        if (Array.isArray(error.detail)) {
          message = error.detail.map((e: any) => e.msg).join(', ');
        } else if (typeof error.detail === 'string') {
          message = error.detail;
        }
        throw new Error(message);
      }

      const data = await response.json();
      setPricingData(data.config);
      setShowPricingModal(false);
      setSuccessMessage(`Custom pricing saved for ${pricingSession.restaurant_name}`);
      setShowSuccessModal(true);
    } catch (err: any) {
      console.error('Failed to save pricing:', err);
      setErrorMessage(err.message || 'Failed to save pricing configuration');
      setShowErrorModal(true);
    } finally {
      setSavingPricing(false);
    }
  };

  // Handle resetting pricing to defaults
  const handleResetPricing = async () => {
    if (!pricingSession?.rest_account_id) return;

    setSavingPricing(true);

    try {
      const response = await fetch(`/api/sales/pricing/${pricingSession.rest_account_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sales_token')}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reset pricing');
      }

      const data = await response.json();
      setPricingData(data.config);
      setPricingEdits({
        tier_1_fee: data.config.tier_1_fee.toFixed(2),
        tier_2_fee: data.config.tier_2_fee.toFixed(2),
        tier_3_fee: data.config.tier_3_fee.toFixed(2),
        overage_rate: data.config.overage_rate.toFixed(2),
        minutes_included_override: '',
        notes: ''
      });
      setSuccessMessage(`Pricing reset to defaults for ${pricingSession.restaurant_name}`);
      setShowSuccessModal(true);
      setShowPricingModal(false);
    } catch (err: any) {
      console.error('Failed to reset pricing:', err);
      setErrorMessage(err.message || 'Failed to reset pricing');
      setShowErrorModal(true);
    } finally {
      setSavingPricing(false);
    }
  };

  // Pagination
  const totalPages = sessions?.pages || 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Sessions</h1>
        <p className="mt-2 text-white/60">
          Manage all your pre-onboarding sessions
        </p>
      </div>

      {/* Filters Bar */}
      <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <input
              type="text"
              placeholder="Search restaurant name..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60" />
            <select
              value={filterStatus}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent appearance-none cursor-pointer"
            >
              <option value="all" className="bg-[#1f4ed1] text-white">All Status</option>
              <option value="pending" className="bg-[#1f4ed1] text-white">Pending</option>
              <option value="activated" className="bg-[#1f4ed1] text-white">Activated</option>
              <option value="expired" className="bg-[#1f4ed1] text-white">Expired</option>
              <option value="ready" className="bg-[#1f4ed1] text-white">Ready</option>
              <option value="processing" className="bg-[#1f4ed1] text-white">Processing</option>
              <option value="failed" className="bg-[#1f4ed1] text-white">Failed</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
          </div>

          {/* Sort */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => handleSortChange(e.target.value)}
              className="w-full px-4 pr-10 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#FDB913] focus:border-transparent appearance-none cursor-pointer"
            >
              <option value="newest" className="bg-[#1f4ed1] text-white">Newest First</option>
              <option value="oldest" className="bg-[#1f4ed1] text-white">Oldest First</option>
              <option value="name" className="bg-[#1f4ed1] text-white">Name (A-Z)</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/60 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-white/60 animate-spin mx-auto" />
            <p className="mt-4 text-white/60">Loading sessions...</p>
          </div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-300 mx-auto" />
            <p className="mt-4 text-red-300 text-lg font-medium">Error Loading Sessions</p>
            <p className="mt-2 text-white/60">{error}</p>
            <button
              onClick={() => loadSessions()}
              className="mt-6 px-4 py-2 bg-[#FDB913] text-black rounded-lg hover:bg-[#FDB913]/90 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      ) : sessions && sessions.sessions.length === 0 ? (
        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-12 text-center">
          <FileText className="w-12 h-12 text-white/30 mx-auto" />
          <p className="mt-4 text-white/60">No sessions found</p>
          {(searchQuery || filterStatus !== 'all') && (
            <p className="text-white/40 text-sm mt-1">Try adjusting your search or filters</p>
          )}
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-white/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Restaurant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Items
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Activation Link
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white/60 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {sessions?.sessions.map((session, index) => (
                    <motion.tr
                      key={session.session_id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                      className="hover:bg-white/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          {session.restaurant_name}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(session.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white/80">
                          {session.menu_item_count || 0} items
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {session.activation_link ? (
                          <button
                            onClick={async () => {
                              await navigator.clipboard.writeText(session.activation_link);
                              setLinkCopied(true);
                              setTimeout(() => setLinkCopied(false), 2000);
                            }}
                            className="flex items-center gap-2 px-3 py-1.5 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 text-[#FDB913] rounded-lg transition-colors text-xs group"
                            title={session.activation_link}
                          >
                            <LinkIcon className="w-3 h-3" />
                            <span className="max-w-[100px] truncate">{session.activation_link.split('/').pop()?.substring(0, 12)}...</span>
                            <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </button>
                        ) : (
                          <span className="text-white/40 text-xs">Not generated</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white/80">
                          {formatDate(session.created_at)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* Preview Demo button - For pending/ready sessions (shows demo experience) */}
                          {session.rest_account_id && (session.status === 'ready' || session.status === 'pending') && (
                            <button
                              onClick={() => handleOpenDashboard(session)}
                              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                              title="Preview Demo"
                            >
                              <Eye className="w-4 h-4 text-white" />
                            </button>
                          )}

                          {/* Edit Menu button - Available for ALL statuses with menu items */}
                          {session.menu_item_count > 0 && (
                            <button
                              onClick={() => handleEditMenu(session)}
                              className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors"
                              title="Edit Menu"
                            >
                              <Edit className="w-4 h-4 text-purple-400" />
                            </button>
                          )}

                          {/* Generate Invite button - Only for ready sessions */}
                          {session.status === 'ready' && (
                            <button
                              onClick={() => handleGenerateInvite(session)}
                              className="p-2 bg-[#FDB913]/20 hover:bg-[#FDB913]/30 rounded-lg transition-colors"
                              title="Generate Invite"
                            >
                              <Send className="w-4 h-4 text-[#FDB913]" />
                            </button>
                          )}

                          {/* Renew Invite button - Only for expired sessions */}
                          {session.status === 'expired' && (
                            <button
                              onClick={() => handleRenewInvite(session)}
                              className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg transition-colors"
                              title="Renew Invite (extend 7 more days)"
                            >
                              <RefreshCw className="w-4 h-4 text-cyan-400" />
                            </button>
                          )}

                          {/* Delete button - Only for non-activated (protect claimed accounts) */}
                          {session.status !== 'activated' && (
                            <button
                              onClick={() => handleDelete(session)}
                              className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                              title="Delete Session"
                            >
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </button>
                          )}

                          {/* Admin Dashboard button - Only for activated restaurants */}
                          {session.status === 'activated' && session.rest_account_id && (
                            <button
                              onClick={() => handleAdminDashboard(session)}
                              className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
                              title="Admin Dashboard"
                            >
                              <LayoutDashboard className="w-4 h-4 text-green-400" />
                            </button>
                          )}

                          {/* Upsell: Add Virtual Host - Only for activated sessions with VA but not VH */}
                          {session.status === 'activated' && session.has_virtual_assistant && !session.has_virtual_host && session.has_stripe_customer && (
                            <button
                              onClick={() => handleUpsellVirtualHost(session)}
                              className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors"
                              title="Add Virtual Host"
                            >
                              <Phone className="w-4 h-4 text-blue-400" />
                            </button>
                          )}

                          {/* Upsell: Add Virtual Assistant - Only for activated sessions with VH but not VA */}
                          {session.status === 'activated' && session.has_virtual_host && !session.has_virtual_assistant && session.has_stripe_customer && (
                            <button
                              onClick={() => handleUpsellVirtualAssistant(session)}
                              className="p-2 bg-orange-500/20 hover:bg-orange-500/30 rounded-lg transition-colors"
                              title="Add Virtual Assistant"
                            >
                              <Plus className="w-4 h-4 text-orange-400" />
                            </button>
                          )}

                          {/* Custom Pricing - For all sessions with rest_account_id */}
                          {session.rest_account_id && (
                            <button
                              onClick={() => handleOpenPricing(session)}
                              className="p-2 bg-emerald-500/20 hover:bg-emerald-500/30 rounded-lg transition-colors"
                              title="Configure Custom Pricing"
                            >
                              <DollarSign className="w-4 h-4 text-emerald-400" />
                            </button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {sessions && sessions.pages > 1 && (
              <div className="px-6 py-4 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-white/60">
                    Showing page {sessions.page} of {sessions.pages} ({sessions.total} total)
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={!canGoPrevious}
                      className={`
                        p-2 rounded-lg transition-colors
                        ${canGoPrevious
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-white/5 text-white/30 cursor-not-allowed'
                        }
                      `}
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                    {/* Page numbers */}
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`
                            w-10 h-10 rounded-lg transition-colors
                            ${pageNum === currentPage
                              ? 'bg-[#FDB913] text-black font-medium'
                              : 'bg-white/10 hover:bg-white/20 text-white'
                            }
                          `}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={!canGoNext}
                      className={`
                        p-2 rounded-lg transition-colors
                        ${canGoNext
                          ? 'bg-white/10 hover:bg-white/20 text-white'
                          : 'bg-white/5 text-white/30 cursor-not-allowed'
                        }
                      `}
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Activation Link Modal */}
      <AnimatePresence>
        {showActivationModal && activationLinkData && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gradient-to-br from-[#1f4ed1] to-[#2a5de0] border border-white/20 rounded-2xl p-8 max-w-2xl w-full shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#FDB913]/20 rounded-full flex items-center justify-center">
                    <LinkIcon className="w-6 h-6 text-[#FDB913]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Activation Link Generated</h2>
                    <p className="text-white/60 text-sm">For {activationLinkData.restaurantName}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowActivationModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              {/* Activation Link Display */}
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1 overflow-hidden">
                    <p className="text-white/60 text-xs mb-1">Activation Link:</p>
                    <p className="text-white font-mono text-sm break-all">{activationLinkData.link}</p>
                  </div>
                  <button
                    onClick={handleCopyLink}
                    className="flex items-center gap-2 px-4 py-2 bg-[#FDB913] hover:bg-[#FDB913]/90 text-black font-medium rounded-lg transition-colors whitespace-nowrap"
                  >
                    {linkCopied ? (
                      <>
                        <Check className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy Link
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Expiration Info */}
              <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-4 mb-6">
                <p className="text-blue-300 text-sm">
                  <strong>Valid for 7 days</strong> - Expires on {new Date(activationLinkData.expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              </div>

              {/* Send Email Section */}
              <div className="bg-white/10 backdrop-blur-lg border border-white/20 rounded-xl p-4 mb-6">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Activation Email
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-white/60 text-xs block mb-1">Restaurant Owner Email:</label>
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="owner@restaurant.com"
                      className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#FDB913]/50"
                      disabled={sendingEmail}
                    />
                  </div>
                  {emailError && (
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {emailError}
                    </p>
                  )}
                  {emailSent && (
                    <p className="text-green-400 text-sm flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      Email sent successfully to {customerEmail}!
                    </p>
                  )}
                  <button
                    onClick={handleSendEmail}
                    disabled={!customerEmail || sendingEmail}
                    className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 disabled:bg-green-500/50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {sendingEmail ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : emailSent ? (
                      <>
                        <Send className="w-4 h-4" />
                        Send Again
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Email
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="space-y-3 mb-6">
                <h3 className="text-white font-semibold">What Happens Next:</h3>
                <ol className="list-decimal list-inside space-y-2 text-white/80 text-sm">
                  <li>Restaurant owner receives branded activation email</li>
                  <li>They click the link and sign in with Google/Facebook/Apple</li>
                  <li>They select a subscription plan and complete payment</li>
                  <li>Their account is activated automatically!</li>
                </ol>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowActivationModal(false)}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleCopyLink}
                  className="flex-1 px-6 py-3 bg-[#FDB913] hover:bg-[#FDB913]/90 text-black font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copy Link
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white dark:bg-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 dark:text-red-400">
              Delete Session?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-300">
              {sessionToDelete && (
                <>
                  <p className="font-medium text-gray-900 dark:text-white mb-3">
                    Delete "{sessionToDelete.restaurant_name}"?
                  </p>
                  <p className="mb-2">This will permanently remove:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li>Extracted menu data ({sessionToDelete.menu_item_count || 0} items)</li>
                    <li>Uploaded files</li>
                    <li>Demo account access</li>
                    <li>Redis cache</li>
                  </ul>
                  <p className="mt-3 font-semibold text-red-600 dark:text-red-400">
                    This cannot be undone!
                  </p>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDelete}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-500 hover:bg-red-600 text-white font-semibold"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error Modal */}
      <AlertDialog open={showErrorModal} onOpenChange={setShowErrorModal}>
        <AlertDialogContent className="bg-white dark:bg-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600 dark:text-red-400">
              Error
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-300">
              {errorMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setShowErrorModal(false)}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Modal */}
      <AlertDialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <AlertDialogContent className="bg-white dark:bg-gray-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-green-600 dark:text-green-400">
              Success
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-300">
              {successMessage}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={() => setShowSuccessModal(false)}
              className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 font-semibold"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pricing Configuration Modal */}
      <AnimatePresence>
        {showPricingModal && pricingSession && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gradient-to-br from-[#1f4ed1] to-[#2a5de0] border border-white/20 rounded-2xl p-8 max-w-lg w-full shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Custom Pricing</h2>
                    <p className="text-white/60 text-sm">{pricingSession.restaurant_name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-white/60" />
                </button>
              </div>

              {loadingPricing ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-white/60 animate-spin" />
                </div>
              ) : pricingData && (
                <>
                  {/* Custom Pricing Badge */}
                  {pricingData.is_custom && (
                    <div className="bg-emerald-500/20 border border-emerald-400/30 rounded-lg px-3 py-2 mb-4 flex items-center gap-2">
                      <Check className="w-4 h-4 text-emerald-400" />
                      <span className="text-emerald-300 text-sm font-medium">Custom pricing active</span>
                    </div>
                  )}

                  {/* Default Pricing Info */}
                  <div className="bg-white/10 rounded-lg p-3 mb-4">
                    <p className="text-white/60 text-xs mb-1">Default Pricing:</p>
                    <p className="text-white/80 text-sm">
                      99¢ (under $30) • $1.49 ($30-$75) • $1.99 (over $75) • 10¢/min overage
                    </p>
                  </div>

                  {/* Fee Inputs */}
                  <div className="space-y-4 mb-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-xs block mb-1">
                          Tier 1 Fee (under $30)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.75"
                            value={pricingEdits.tier_1_fee}
                            onChange={(e) => setPricingEdits(prev => ({ ...prev, tier_1_fee: e.target.value }))}
                            className="w-full pl-7 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                            disabled={savingPricing}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs block mb-1">
                          Tier 2 Fee ($30-$75)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.75"
                            value={pricingEdits.tier_2_fee}
                            onChange={(e) => setPricingEdits(prev => ({ ...prev, tier_2_fee: e.target.value }))}
                            className="w-full pl-7 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                            disabled={savingPricing}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-white/60 text-xs block mb-1">
                          Tier 3 Fee (over $75)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.75"
                            value={pricingEdits.tier_3_fee}
                            onChange={(e) => setPricingEdits(prev => ({ ...prev, tier_3_fee: e.target.value }))}
                            className="w-full pl-7 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                            disabled={savingPricing}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-white/60 text-xs block mb-1">
                          Overage Rate (/min)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/60">$</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0.05"
                            value={pricingEdits.overage_rate}
                            onChange={(e) => setPricingEdits(prev => ({ ...prev, overage_rate: e.target.value }))}
                            className="w-full pl-7 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                            disabled={savingPricing}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="text-white/60 text-xs block mb-1">
                        Monthly Minutes Override (optional — plan default: 2,750)
                      </label>
                      <input
                        type="number"
                        step="100"
                        min="100"
                        max="10000"
                        value={pricingEdits.minutes_included_override}
                        onChange={(e) => setPricingEdits(prev => ({ ...prev, minutes_included_override: e.target.value }))}
                        placeholder="e.g., 4000 — leave blank for plan default"
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                        disabled={savingPricing}
                      />
                    </div>

                    <div>
                      <label className="text-white/60 text-xs block mb-1">
                        Notes (optional)
                      </label>
                      <input
                        type="text"
                        value={pricingEdits.notes}
                        onChange={(e) => setPricingEdits(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="e.g., HBK franchise deal, 30 locations"
                        className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-emerald-400/50"
                        maxLength={500}
                        disabled={savingPricing}
                      />
                    </div>
                  </div>

                  {/* Tip for flat fee */}
                  <div className="bg-blue-500/10 border border-blue-400/20 rounded-lg p-3 mb-6">
                    <p className="text-blue-300 text-xs">
                      <strong>Tip:</strong> For a flat fee, enter the same value in all three tiers (e.g., $0.85 everywhere for a flat 85¢ fee).
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {pricingData.is_custom && (
                      <button
                        onClick={handleResetPricing}
                        disabled={savingPricing}
                        className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 font-medium rounded-lg transition-colors disabled:opacity-50"
                      >
                        Reset to Defaults
                      </button>
                    )}
                    <button
                      onClick={() => setShowPricingModal(false)}
                      disabled={savingPricing}
                      className="flex-1 px-4 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSavePricing}
                      disabled={savingPricing}
                      className="flex-1 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {savingPricing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4" />
                          Save Pricing
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SalesSessionsList;
